import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAccount } from "@/hooks/use-account";
import { ProfileCard } from "@/components/site/ProfileCard";
import { MyQuotesPanel } from "@/components/site/MyQuotesPanel";
import { DashboardOverview } from "@/components/dashboard/DashboardOverview";
import { NotificationsPanel } from "@/components/site/NotificationsPanel";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({
    meta: [{ title: "Your dashboard | Finendra" }, { name: "robots", content: "noindex" }],
  }),
  component: DashboardPage,
});

type Tab = "overview" | "quotes" | "files" | "notifications" | "profile";

function DashboardPage() {
  const account = useAccount();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("overview");

  async function signOut() {
    await supabase.auth.signOut();
    await navigate({ to: "/auth", replace: true });
  }

  if (account.loading) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6">
        <p className="text-brand-navy/60">Loading your dashboard…</p>
      </main>
    );
  }

  return (
    <main className="min-h-screen px-6 pt-24 md:pt-32 pb-28">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-brand-accent">
              Member area
            </p>
            <h1 className="mt-3 font-display text-4xl font-bold text-brand-navy">
              {account.displayName ? `Welcome, ${account.displayName}` : "Your dashboard"}
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            {account.isAdmin && (
              <Link
                to="/admin"
                className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent"
              >
                Admin console
              </Link>
            )}
            <Link
              to="/files"
              className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent"
            >
              Files
            </Link>
            <Link
              to="/pricing"
              className="rounded-xl border border-brand-navy/15 px-5 py-3 text-sm font-bold text-brand-navy hover:border-brand-accent hover:text-brand-accent"
            >
              New quote
            </Link>
            <button
              type="button"
              onClick={signOut}
              className="rounded-xl bg-brand-navy px-5 py-3 text-sm font-bold text-background hover:bg-brand-accent"
            >
              Sign out
            </button>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-2 border-b border-brand-navy/10 pb-4">
          {(
            [
              ["overview", "Overview"],
              ["quotes", "My quotes"],
              ["notifications", "Notifications"],
              ["profile", "Profile"],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              aria-current={tab === id ? "page" : undefined}
              className={`rounded-full px-5 py-2 text-sm font-bold transition-colors ${
                tab === id
                  ? "bg-brand-navy text-background"
                  : "border border-brand-navy/15 text-brand-navy/70 hover:text-brand-accent"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "overview" ? (
          <DashboardOverview account={account} onOpenTab={setTab} />
        ) : tab === "quotes" && account.userId ? (
          <MyQuotesPanel userId={account.userId} />
        ) : tab === "notifications" ? (
          <NotificationsPanel />
        ) : (
          <ProfileCard account={account} />
        )}
      </div>
    </main>
  );
}
